import { Link, useLocation, useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { API, Auth } from "aws-amplify";
import { Button } from "@aws-amplify/ui-react";
import Loadingmini from "../components/loadingmini";
import Userinfo from "./userinfo";
import Usernotes from "./usernotes";
import Userstatushistory from "./userstatushistory";
import Userreservations from "./userreservations";
import Usercredits from "./usercredits";
import Userdeposits from "./userdeposits";
import Userincidentals from "./userincidentals";
import Usercommercialplatformaccounts from "./usercommercialplatformaccounts";
import Userdocs from "./userdocs";
import Userpersonalinsurance from "./userpersonalinsurance";
import Userpricing from "./userpricing";
import Userchathistory from "./userchathistory";
import Usercommercialplatformactivity from "./usercommercialplatformactivity";
import DriverRateCard from "../components/driverratecard";
import { fleetFromLocation, fetchStaffSummary, money, platformState, reservationPagePath } from "../components/staffpage";
import { driverProfileQuery } from "../driverProfileQuery";
import { driverGigActivityQuery } from "../driverGigActivityQuery";
import { withFleets } from "../withFleets";
import "../components/staffpage.css";

const SECTIONS = [
  ["info", "Info"],
  ["reservations", "Reservations"],
  ["pricing", "Pricing"],
  ["money", "Money"],
  ["gig", "Gig"],
  ["docs", "Docs"],
  ["notes", "Notes"],
  ["history", "History"],
  ["insurance", "Insurance"],
  ["chat", "Chat"],
  ["json", "JSON"],
];

function firstFleet(value) {
  if (Array.isArray(value)) return value.find((item) => item) || "";
  return value || "";
}

export default function DriverNew({ userId: userIdProp, fleetCode: fleetProp }) {
  const params = useParams();
  const location = useLocation();
  const driverId = userIdProp || params.userId;
  const fleetCode = firstFleet(fleetProp) || fleetFromLocation(location.search).split(",")[0];
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const requestedSection = new URLSearchParams(location.search).get("section") || "";
  const sectionIsKnown = SECTIONS.some(([id]) => id === requestedSection);
  const [section, setSection] = useState(sectionIsKnown ? requestedSection : "");
  const [profile, setProfile] = useState(null);
  const [profileError, setProfileError] = useState("");
  const [gig, setGig] = useState(null);
  const [gigError, setGigError] = useState("");
  const [jwt, setJwt] = useState("");
  const [pricingRevision, setPricingRevision] = useState(0);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const session = await Auth.currentSession();
      setJwt(session.getAccessToken().getJwtToken());
      if (!fleetCode) {
        setError("Select a fleet, then open this driver again.");
        setSummary(null);
        return;
      }
      const body = await fetchStaffSummary({ fleetCode, driverId });
      if (!body || body.status !== "success") {
        setError((body && body.message) || "Could not load this driver.");
        setSummary(null);
        return;
      }
      setSummary(body);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [driverId, fleetCode]);

  async function loadProfile() {
    setProfileError("");
    try {
      const session = await Auth.currentSession();
      const token = session.getAccessToken().getJwtToken();
      setJwt(token);
      const res = await API.get("adminWebTools", "/ago", { queryStringParameters: driverProfileQuery({
        driverId,
        fleetCode,
        jwt: token,
        backEnd: process.env.REACT_APP_BACKEND,
      })});
      if (!res || res.status === "error" || !Array.isArray(res.resp)) {
        setProfile(null);
        setProfileError((res && res.message) || "Could not load driver info.");
        return res;
      }
      setProfile(res);
      return res;
    } catch (e) {
      setProfile(null);
      setProfileError("Could not load driver info.");
      return null;
    }
  }

  async function refreshJwt() {
    const session = await Auth.currentSession();
    setJwt(session.getAccessToken().getJwtToken());
  }

  async function loadGig() {
    setGigError("");
    try {
      const session = await Auth.currentSession();
      const token = session.getAccessToken().getJwtToken();
      setJwt(token);
      const res = await API.get("adminWebTools", "/ago", { queryStringParameters: driverGigActivityQuery({
        driverId,
        fleetCode,
        jwt: token,
        backEnd: process.env.REACT_APP_BACKEND,
      })});
      if (!res || res.status === "error" || !Array.isArray(res.resp)) {
        setGig(null);
        setGigError((res && res.message) || "Could not load gig activity.");
        return res;
      }
      setGig(res);
      return res;
    } catch (e) {
      setGig(null);
      setGigError("Could not load gig activity.");
      return null;
    }
  }

  function sameDriver(payload) {
    const row = payload && payload.resp && payload.resp[0];
    return row && String(row.id) === String(driverId);
  }

  function choose(id) {
    setSection(id);
    if ((id === "info" || id === "json") && !sameDriver(profile)) loadProfile();
    if (id === "gig" && !sameDriver(gig)) loadGig();
  }

  useEffect(() => {
    if (!sectionIsKnown) return;
    choose(requestedSection);
  }, [requestedSection, driverId]);

  const driver = summary && summary.driver;
  const attention = driver && driver.attention;
  const platforms = (driver && driver.platforms) || [];

  return (
    <div className="staff-page">
      <header className="staff-head">
        <p className="staff-kicker">Driver {driverId}</p>
        <h1 className="staff-title">{driver ? driver.name : "Driver"}</h1>
        {attention && attention.sentence ? <p className="staff-sentence">{attention.sentence}</p> : null}
        {driver ? (
          <div className="staff-chips">
            {driver.phone ? (
              <a className="staff-fact" href={"tel:" + driver.phone}><span className="k">Phone</span><span className="v">{driver.phone}</span></a>
            ) : null}
            {money(driver.credits) !== null ? (
              <button type="button" className="staff-fact" onClick={() => choose("money")}><span className="k">Credits</span><span className="v">{money(driver.credits)}</span></button>
            ) : null}
            {money(driver.depositOnHand) !== null ? (
              <button type="button" className="staff-fact" onClick={() => choose("money")}><span className="k">Deposit on hand</span><span className="v">{money(driver.depositOnHand)}</span></button>
            ) : null}
            {driver.mvrStatus ? (
              <span className="staff-fact"><span className="k">MVR</span><span className="v">{driver.mvrStatus}</span></span>
            ) : null}
            {platforms.length ? platforms.map((row) => (
              <button type="button" className={"staff-fact" + (String(row.verification || "").toLowerCase().includes("verif") ? "" : " alert")} key={row.platform + row.connection} onClick={() => choose("gig")}>
                <span className="k">{row.platform || "Platform"}</span>
                <span className="v">{platformState(row)}</span>
              </button>
            )) : driver.platformsReady ? (
              <button type="button" className="staff-fact alert" onClick={() => choose("gig")}><span className="k">Gig platform</span><span className="v">Not linked</span></button>
            ) : null}
            {driver.openReservationId ? (
              <Link className="staff-fact" to={withFleets("/reservation/" + driver.openReservationId, fleetCode)}><span className="k">Open reservation</span><span className="v">{driver.openReservationId}</span></Link>
            ) : null}
            {driver.coverage && driver.openReservationId ? (
              <Link className={"staff-fact wide" + (driver.coverage.kind === "none" ? " alert" : "")} to={reservationPagePath(driver.openReservationId, fleetCode, "insurance")}>
                <span className="k">Coverage</span>
                <span className="v">{driver.coverage.line}</span>
              </Link>
            ) : null}
          </div>
        ) : null}
        <div className="staff-chips">
          <Button size="small" onClick={load}>Refresh</Button>
        </div>
        {summary && summary.attentionReady === false ? <p className="staff-note">Next step did not load.</p> : null}
        {error ? <p className="staff-note">{error}</p> : null}
      </header>
      {loading ? <Loadingmini /> : (
        <>
          <nav className="staff-actions">
            {SECTIONS.map(([id, label]) => (
              <button type="button" key={id} className={section === id ? "on" : ""} onClick={() => choose(id)}>{label}</button>
            ))}
          </nav>
          <div className="staff-body">
            {(section === "info" || section === "json") && profileError ? <p className="staff-note">{profileError}</p> : null}
            {section === "gig" && gigError ? <p className="staff-note">{gigError}</p> : null}
            {section === "info" && profile ? <Userinfo userJwt={jwt} user={profile} getUpdatedUserData={loadProfile} fleetCode={fleetCode} displayAdministrativeFunctions={true} /> : null}
            {section === "notes" ? <Usernotes userJwt={jwt} userId={driverId} fleetCode={fleetCode} /> : null}
            {section === "history" ? <Userstatushistory userJwt={jwt} refreshJwt={() => {}} userId={driverId} fleetCode={fleetCode} displayAdministrativeFunctions={true} /> : null}
            {section === "reservations" ? <Userreservations userJwt={jwt} refreshJwt={() => {}} userId={driverId} fleetCode={fleetCode} /> : null}
            {section === "pricing" && fleetCode && driverId ? (
              <>
                <DriverRateCard fleetCode={fleetCode} driverId={driverId} reloadKey={pricingRevision} />
                {jwt ? (
                  <Userpricing
                    userJwt={jwt}
                    refreshJwt={refreshJwt}
                    userId={driverId}
                    fleetCode={fleetCode}
                    isAdminOrFleet={true}
                    onChanged={() => setPricingRevision((n) => n + 1)}
                  />
                ) : null}
              </>
            ) : null}
            {section === "money" ? (
              <>
                <Usercredits userJwt={jwt} refreshJwt={() => {}} userId={driverId} fleetCode={fleetCode} isAdminOrFleet={true} />
                <Userdeposits userJwt={jwt} refreshJwt={() => {}} userId={driverId} fleetCode={fleetCode} />
                <Userincidentals userId={driverId} fleetCode={fleetCode} />
              </>
            ) : null}
            {section === "gig" && gig ? (
              <>
                <Usercommercialplatformaccounts user={gig} fleetCode={fleetCode} />
                <Usercommercialplatformactivity
                  user={gig}
                  selectCommercialPlatformActivityMaxDate={() => {}}
                  commercialPlatformActivityMaxDate={null}
                />
              </>
            ) : null}
            {section === "gig" && !gig && !gigError ? <Loadingmini /> : null}
            {section === "docs" ? <Userdocs userJwt={jwt} userId={driverId} fleetCode={fleetCode} /> : null}
            {section === "insurance" ? <Userpersonalinsurance userJwt={jwt} userId={driverId} fleetCode={fleetCode} /> : null}
            {section === "chat" ? (
              <Userchathistory
                userJwt={jwt}
                refreshJwt={refreshJwt}
                displayAdministrativeFunctions={true}
                userId={driverId}
                userName={(driver && driver.name) || ("Driver " + driverId)}
                fleetCode={fleetCode}
              />
            ) : null}
            {section === "json" && profile ? <pre className="staff-json">{JSON.stringify(profile, null, 2)}</pre> : null}
            {section === "json" && !profile ? <Loadingmini /> : null}
          </div>
        </>
      )}
    </div>
  );
}
